<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { link } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import {
        agentSubjectKey,
        getAgentSubjectIcon,
        getAgentSubjectLabel,
    } from './adminListAgents.js';

    interface AiJob {
        uuid: string;
        user_uuid: string;
        user: { uuid: string; first_name: string; last_name: string; email: string } | null;
        subject_type: string;
        subject_uuid: string;
        subject_name?: string | null;
        subject_path?: string;
        status: string;
        error_message: string | null;
        duration_seconds: number | null;
        total_tokens: number | null;
        estimated_cost: number | null;
        ai_model: string | null;
        started_at: string | null;
        finished_at: string | null;
        created_at: string;
    }

    const STATUS_LABELS: Record<string, string> = {
        'queued': 'Queued',
        'processing': 'Processing',
        'completed': 'Completed',
        'failed': 'Failed'
    };

    const STATUS_COLORS: Record<string, string> = {
        'queued': 'var(--status-warn)',
        'processing': 'var(--status-info)',
        'completed': 'var(--status-success)',
        'failed': 'var(--status-danger)'
    };

    const STATUS_OPTIONS = [
        { value: '', label: 'All Statuses' },
        { value: 'queued', label: 'Queued' },
        { value: 'processing', label: 'Processing' },
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' }
    ];

    const SUBJECT_OPTIONS = [
        { value: '', label: 'All Types' },
        { value: 'brief', label: 'Built-to-Brief' },
        { value: 'assessment', label: 'Illuminator' },
        { value: 'gapfinder', label: 'GapFinder' }
    ];

    let items: AiJob[] = [];
    let isLoading = false;
    let statusFilter = '';
    let subjectTypeFilter = '';
    let fromDate = '';
    let toDate = '';
    let paging = { current_page: 0, last_page: 1, per_page: 20, total: 0, from: 0, to: 0 };
    let currentPage = 1;
    let selectedJob: AiJob | null = null;
    let showDetailModal = false;
    let autoRefreshTimer: ReturnType<typeof setInterval> | null = null;

    let userSearchQuery = '';
    let userSearchResults: Array<{ uuid: string; first_name: string; last_name: string; email: string }> = [];
    let selectedUser: { uuid: string; first_name: string; last_name: string; email: string } | null = null;
    let userFilter = '';
    let showUserDropdown = false;
    let userSearchDebounceTimer: ReturnType<typeof setTimeout>;

    onMount(async () => {
        await loadJobs();
        autoRefreshTimer = setInterval(() => {
            const hasActive = items.some(j => j.status === 'queued' || j.status === 'processing');
            if (hasActive) loadJobs(true);
        }, 10000);
    });

    onDestroy(() => {
        if (autoRefreshTimer) clearInterval(autoRefreshTimer);
    });

    async function loadJobs(silent = false) {
        if (!silent) isLoading = true;
        try {
            const params: Record<string, string | number> = {
                page: currentPage,
                per_page: 20
            };
            if (statusFilter) params.status = statusFilter;
            if (subjectTypeFilter) params.subject_type = subjectTypeFilter;
            if (userFilter) params.user_uuid = userFilter;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const response: any = await api.getAdminAiJobs(params);
            if (response.success) {
                items = response.items || [];
                paging = response.paging || paging;
            } else {
                if (!silent) toast.showNotification(false, response.message || 'Failed to load AI jobs');
                items = [];
            }
        } catch (error) {
            if (!silent) toast.showNotification(false, 'Error loading AI jobs');
            console.error(error);
            items = [];
        } finally {
            isLoading = false;
        }
    }

    async function searchUsers() {
        if (!userSearchQuery || userSearchQuery.length < 2) {
            userSearchResults = [];
            return;
        }
        try {
            const response: any = await api.makeRequest('GET', `/api/v1/user?phrase=${encodeURIComponent(userSearchQuery)}&per_page=20`);
            if (response.success && response.data) {
                userSearchResults = Array.isArray(response.data) ? response.data : [response.data];
            } else {
                userSearchResults = [];
            }
        } catch {
            userSearchResults = [];
        }
    }

    function debounceUserSearch() {
        clearTimeout(userSearchDebounceTimer);
        userSearchDebounceTimer = setTimeout(() => {
            searchUsers();
        }, 300);
    }

    function selectUser(user: { uuid: string; first_name: string; last_name: string; email: string }) {
        selectedUser = user;
        userFilter = user.uuid;
        userSearchQuery = '';
        userSearchResults = [];
        showUserDropdown = false;
        currentPage = 1;
        loadJobs();
    }

    function clearUserFilter() {
        selectedUser = null;
        userFilter = '';
        currentPage = 1;
        loadJobs();
    }

    function applyFilters() {
        currentPage = 1;
        loadJobs();
    }

    function clearFilters() {
        statusFilter = '';
        subjectTypeFilter = '';
        fromDate = '';
        toDate = '';
        userFilter = '';
        selectedUser = null;
        currentPage = 1;
        loadJobs();
    }

    function formatDate(dateString: string | null): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString(navigator.language, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    }

    function formatDuration(seconds: number | null): string {
        if (seconds === null || seconds === undefined) return '—';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    }

    function getStatusLabel(status: string): string {
        return STATUS_LABELS[status] || status;
    }

    function getStatusColor(status: string): string {
        return STATUS_COLORS[status] || 'var(--grey-secondary)';
    }

    function getSubjectLabel(subjectType: string): string {
        return getAgentSubjectLabel(subjectType);
    }

    function getItemDisplayName(job: AiJob): string {
        const n = job.subject_name?.trim();
        if (n) return n;
        return `Open ${getSubjectLabel(job.subject_type)}`;
    }

    /** Briefs use admin route; other agents use the same in-app paths as members. */
    function getAdminItemPath(job: AiJob): string {
        if (job.subject_type === 'brief') {
            return `/admin/briefs/${job.subject_uuid}`;
        }
        return job.subject_path || '';
    }

    function openDetail(job: AiJob) {
        selectedJob = job;
        showDetailModal = true;
    }

    function closeDetailModal() {
        showDetailModal = false;
        selectedJob = null;
    }

    function handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            closeDetailModal();
            showUserDropdown = false;
        }
    }

    function handleClickOutside(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (showUserDropdown && !target.closest('.user-filter')) {
            showUserDropdown = false;
        }
    }

    function goToPage(page: number) {
        currentPage = page;
        loadJobs();
    }

    $: hasFilters = statusFilter || subjectTypeFilter || userFilter || fromDate || toDate;
    $: activeCount = items.filter(j => j.status === 'queued' || j.status === 'processing').length;
    $: failedCount = items.filter(j => j.status === 'failed').length;
</script>

<svelte:window on:keydown={handleKeydown} on:click={handleClickOutside} />

<div class="page-shell admin-list">
    <div class="page-header">
        <div class="header-content">
            <h2 class="page-title">AI Processes</h2>
            <p class="page-subtitle">All background AI generation jobs across accounts</p>
        </div>
        <div class="header-stats">
            {#if activeCount > 0}
                <span class="stat-badge stat-badge--info">{activeCount} active</span>
            {/if}
            {#if failedCount > 0}
                <span class="stat-badge stat-badge--danger">{failedCount} failed</span>
            {/if}
            <span class="stat-badge">{paging.total} total</span>
        </div>
    </div>

    <div class="filters-bar filters-bar--labeled">
        <div class="filter-group">
            <label for="from-date" class="filter-label">From</label>
            <input
                id="from-date"
                type="date"
                bind:value={fromDate}
                on:change={applyFilters}
                class="filter-input date-input"
            />
        </div>
        <div class="filter-group">
            <label for="to-date" class="filter-label">To</label>
            <input
                id="to-date"
                type="date"
                bind:value={toDate}
                on:change={applyFilters}
                class="filter-input date-input"
            />
        </div>
        <div class="filter-group">
            <label for="status-filter" class="filter-label">Status</label>
            <select id="status-filter" bind:value={statusFilter} on:change={applyFilters} class="filter-select">
                {#each STATUS_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                {/each}
            </select>
        </div>
        <div class="filter-group">
            <label for="type-filter" class="filter-label">Type</label>
            <select id="type-filter" bind:value={subjectTypeFilter} on:change={applyFilters} class="filter-select">
                {#each SUBJECT_OPTIONS as option}
                    <option value={option.value}>{option.label}</option>
                {/each}
            </select>
        </div>
        <div class="filter-group user-filter">
            <label for="user-search" class="filter-label">User</label>
            <div class="user-select-wrapper">
                {#if selectedUser}
                    <div class="selected-user">
                        <span>{selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})</span>
                        <button type="button" class="clear-user-btn" on:click={clearUserFilter} aria-label="Clear user filter">&times;</button>
                    </div>
                {:else}
                    <input
                        id="user-search"
                        type="text"
                        placeholder="Search user..."
                        bind:value={userSearchQuery}
                        on:input={debounceUserSearch}
                        on:focus={() => showUserDropdown = true}
                        class="filter-input"
                    />
                    {#if showUserDropdown && userSearchResults.length > 0}
                        <ul class="user-dropdown" role="listbox">
                            {#each userSearchResults as user (user.uuid)}
                                <li role="option" aria-selected="false" tabindex="0" on:click={() => selectUser(user)} on:keydown={(e) => e.key === 'Enter' && selectUser(user)}>
                                    {user.first_name} {user.last_name} - {user.email}
                                </li>
                            {/each}
                        </ul>
                    {/if}
                {/if}
            </div>
        </div>
        {#if hasFilters}
            <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
        {/if}
        <button type="button" class="refresh-btn" on:click={() => loadJobs()} disabled={isLoading} title="Refresh" aria-label="Refresh">
            <i class="refresh icon" aria-hidden="true"></i>
        </button>
        <span class="results-count">
            {paging.total} {paging.total === 1 ? 'process' : 'processes'}
        </span>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading AI processes...</div>
    {:else if items.length === 0}
        <div class="empty-state">
            <i class="cogs icon empty-icon" aria-hidden="true"></i>
            <p>No AI processes found.</p>
            {#if hasFilters}
                <button type="button" class="clear-filters-btn" on:click={clearFilters}>Clear filters</button>
            {/if}
        </div>
    {:else}
        <div class="table-wrap">
            <table class="data-table">
                <thead>
                    <tr>
                        <th><i class="cog icon" aria-hidden="true"></i> Agent</th>
                        <th><i class="file outline icon" aria-hidden="true"></i> Report</th>
                        <th><i class="signal icon" aria-hidden="true"></i> Status</th>
                        <th><i class="user icon" aria-hidden="true"></i> User</th>
                        <th><i class="calendar plus icon" aria-hidden="true"></i> Created</th>
                        <th class="num-col"><i class="database icon" aria-hidden="true"></i> Tokens</th>
                        <th><i class="clock icon" aria-hidden="true"></i> Duration</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each items as job (job.uuid)}
                        <tr class="clickable-row data-row" on:click={() => openDetail(job)} role="button" tabindex="0" on:keydown={(e) => e.key === 'Enter' && openDetail(job)}>
                            <td>
                                <div class="type-wrapper">
                                    <div class="type-icon" data-subject={agentSubjectKey(job.subject_type)}>
                                        <i class="{getAgentSubjectIcon(job.subject_type)} icon" aria-hidden="true"></i>
                                    </div>
                                    <span class="type-label">{getSubjectLabel(job.subject_type)}</span>
                                </div>
                            </td>
                            <td class="item-cell">
                                {#if getAdminItemPath(job)}
                                    <a
                                        use:link
                                        href={getAdminItemPath(job)}
                                        class="item-link"
                                        title={getItemDisplayName(job)}
                                        on:click|stopPropagation
                                    >
                                        <span class="item-link-text">{getItemDisplayName(job)}</span>
                                        <i class="icon chevron right item-link-icon" aria-hidden="true"></i>
                                    </a>
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td>
                                <span class="color-badge" style="--badge-color: {getStatusColor(job.status)}">
                                    {#if job.status === 'processing'}
                                        <span class="pulse-dot" aria-hidden="true"></span>
                                    {/if}
                                    {getStatusLabel(job.status)}
                                </span>
                            </td>
                            <td class="user-cell">
                                {#if job.user}
                                    <div class="user-name">{job.user.first_name} {job.user.last_name}</div>
                                    <div class="user-email">{job.user.email}</div>
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td class="date-cell">{formatDate(job.created_at)}</td>
                            <td class="num-cell">
                                {#if job.total_tokens != null}
                                    {job.total_tokens.toLocaleString(navigator.language)}
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td class="duration-cell">
                                {#if job.duration_seconds != null}
                                    <span class="duration-badge">{formatDuration(job.duration_seconds)}</span>
                                {:else}
                                    <span class="text-muted">—</span>
                                {/if}
                            </td>
                            <td class="actions-cell">
                                <button type="button" class="view-button" on:click|stopPropagation={() => openDetail(job)}>
                                    <i class="icon eye"></i> View
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        {#if paging.last_page > 1}
            <div class="pagination">
                <span class="pagination-info">
                    Showing {paging.from || 0}–{paging.to || 0} of {paging.total}
                </span>
                <div class="pagination-buttons">
                    <button type="button" class="pagination-btn" disabled={currentPage <= 1} on:click={() => goToPage(currentPage - 1)} aria-label="Previous page">Previous</button>
                    <span class="page-indicator">Page {currentPage} of {paging.last_page}</span>
                    <button type="button" class="pagination-btn" disabled={currentPage >= paging.last_page} on:click={() => goToPage(currentPage + 1)} aria-label="Next page">Next</button>
                </div>
            </div>
        {/if}
    {/if}
</div>

{#if showDetailModal && selectedJob}
    <div
        class="modal-overlay"
        role="dialog"
        aria-labelledby="job-detail-title"
        aria-modal="true"
    >
        <button type="button" class="modal-backdrop" aria-label="Close dialog" on:click={closeDetailModal}></button>
        <div class="modal-content" role="document">
            <div class="modal-header">
                <h3 id="job-detail-title">AI Process Detail</h3>
                <button type="button" class="modal-close" on:click={closeDetailModal} aria-label="Close">&times;</button>
            </div>
            <div class="modal-body">
                <dl class="detail-list">
                    <dt>Agent</dt>
                    <dd>
                        <div class="type-wrapper">
                            <div class="type-icon" data-subject={agentSubjectKey(selectedJob.subject_type)}>
                                <i class="{getAgentSubjectIcon(selectedJob.subject_type)} icon" aria-hidden="true"></i>
                            </div>
                            <span class="type-label">{getSubjectLabel(selectedJob.subject_type)}</span>
                        </div>
                    </dd>

                    <dt>Report</dt>
                    <dd>
                        {#if getAdminItemPath(selectedJob)}
                            <a use:link href={getAdminItemPath(selectedJob)} class="modal-item-link">
                                {getItemDisplayName(selectedJob)}
                            </a>
                        {:else}
                            <span class="text-muted">—</span>
                        {/if}
                    </dd>

                    <dt>Status</dt>
                    <dd>
                        <span class="color-badge" style="--badge-color: {getStatusColor(selectedJob.status)}">
                            {#if selectedJob.status === 'processing'}
                                <span class="pulse-dot"></span>
                            {/if}
                            {getStatusLabel(selectedJob.status)}
                        </span>
                    </dd>

                    <dt>User</dt>
                    <dd>
                        {#if selectedJob.user}
                            {selectedJob.user.first_name} {selectedJob.user.last_name} ({selectedJob.user.email})
                        {:else}
                            —
                        {/if}
                    </dd>

                    <dt>Created</dt>
                    <dd>{formatDate(selectedJob.created_at)}</dd>

                    <dt>Started</dt>
                    <dd>{formatDate(selectedJob.started_at)}</dd>

                    <dt>Finished</dt>
                    <dd>{formatDate(selectedJob.finished_at)}</dd>

                    <dt>Duration</dt>
                    <dd>{formatDuration(selectedJob.duration_seconds)}</dd>

                    <dt>Tokens</dt>
                    <dd>
                        {#if selectedJob.total_tokens != null}
                            {selectedJob.total_tokens.toLocaleString(navigator.language)}
                            {#if selectedJob.estimated_cost != null}
                                <span class="text-muted">
                                    (est. {new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'USD' }).format(selectedJob.estimated_cost)})
                                </span>
                            {/if}
                        {:else}
                            —
                        {/if}
                    </dd>

                    {#if selectedJob.ai_model}
                        <dt>Model</dt>
                        <dd><code class="uuid-display">{selectedJob.ai_model}</code></dd>
                    {/if}

                    {#if selectedJob.error_message}
                        <dt>Error</dt>
                        <dd>
                            <div class="error-block">{selectedJob.error_message}</div>
                        </dd>
                    {/if}

                    <dt>Job ID</dt>
                    <dd><code class="uuid-display">{selectedJob.uuid}</code></dd>

                    <dt>Subject ID</dt>
                    <dd><code class="uuid-display">{selectedJob.subject_uuid}</code></dd>
                </dl>
            </div>
            <div class="modal-footer">
                <button type="button" class="modal-close-btn" on:click={closeDetailModal}>Close</button>
            </div>
        </div>
    </div>
{/if}

<style>
    .item-cell {
        max-width: 14rem;
    }

    .modal-item-link {
        color: var(--primary);
        font-weight: 500;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2000;
    }

    .modal-backdrop {
        position: absolute;
        inset: 0;
        border: none;
        padding: 0;
        margin: 0;
        background: var(--shade-background-color);
        cursor: default;
    }

    .modal-content {
        position: relative;
        z-index: 1;
        background: var(--white);
        border-radius: var(--border-radius-button);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
        max-width: 640px;
        width: 92%;
        max-height: 88vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid var(--border-color);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--grey);
    }

    .modal-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: var(--grey-secondary);
        padding: 0 0.25rem;
    }

    .modal-close:hover {
        color: var(--grey);
    }

    .modal-body {
        overflow-y: auto;
        flex: 1;
        padding: 1.5rem;
    }

    .modal-footer {
        padding: 1rem 1.5rem;
        border-top: 1px solid var(--border-color);
        background: var(--greyish);
        flex-shrink: 0;
    }

    .modal-close-btn {
        display: block;
        width: 100%;
        padding: 0.75rem 1.5rem;
        background: var(--primary);
        color: var(--white);
        border: none;
        border-radius: var(--border-radius-button);
        font-size: 1rem;
        font-weight: 500;
        cursor: pointer;
    }

    .modal-close-btn:hover {
        background: var(--darkprimary);
    }

    .detail-list {
        margin: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 0.5rem 1.5rem;
    }

    .detail-list dt {
        font-weight: 500;
        color: var(--grey-secondary);
    }

    .detail-list dd {
        margin: 0;
    }

    .error-block {
        background: var(--status-danger-soft);
        color: var(--status-danger-ink);
        padding: 0.75rem;
        border-radius: 4px;
        font-size: 0.875rem;
        border: 1px solid color-mix(in srgb, var(--status-danger) 25%, transparent);
    }

    .uuid-display {
        font-size: 0.75rem;
        background: var(--greyish);
        padding: 0.125rem 0.375rem;
        border-radius: 3px;
        word-break: break-all;
    }

    @media (max-width: 768px) {
        .item-cell {
            max-width: 100%;
        }
    }
</style>
